import React from 'react'

import { FormattedMessage } from 'react-intl'

import { Icon } from '@tribegroup'
import { clipboard } from '@utils'
import { IAgentSourcedCreator, Provider } from '../types'
import { resolveCreatorName } from '../utils/resolveCreatorName'
import VibeCheckStatusPill from '../VibeCheckStatusPill/VibeCheckStatusPill'
import {
  ActionStack,
  HeaderActionButton,
  HeaderAvatar,
  HeaderAvatarBlock,
  HeaderCopyButton,
  HeaderEmailRow,
  HeaderEmailValue,
  HeaderInfo,
  HeaderMeta,
  HeaderName,
  HeaderPillRow,
  HeaderPlatformBadge,
  HeaderPlatformPill,
  HeaderRoot,
  HeaderRosterPill,
  HeaderShortlistButton,
} from './Header.styled'

// How long the "Copied" confirmation stands before reverting (BRAND-9635).
const COPIED_RESET_MS = 2000

const PROVIDER_GLYPH: Record<Provider, string> = {
  instagram: 'social-instagram-colour',
  tiktok: 'social-tiktok-colour',
  youtube: 'social-youtube-colour',
}

interface IProps {
  creator: IAgentSourcedCreator
  onApprove?: () => void
  onReject?: () => void
  onUndo?: () => void
  onShortlist?: () => void
  onUnshortlist?: () => void
  isActionLoading?: boolean
  // The owning view decides, exactly as it does for the row — otherwise the drawer
  // would contradict the row it was opened from on List/Shortlisted (BRAND-9587).
  showDecisionPill?: boolean
  // Shows the creator-supplied response email when there is one, else the OnSocial
  // one (BRAND-9722). BRAND-9635: it is internal outreach data, and this drawer is
  // also rendered by the unauthenticated /campaign-creators/:token page, so the
  // default is off — a caller that forgets to opt in fails closed rather than
  // leaking PII.
  showSourcedEmail?: boolean
}

const Header: React.FC<IProps> = ({
  creator,
  onApprove,
  onReject,
  onUndo,
  onShortlist,
  onUnshortlist,
  isActionLoading = false,
  showDecisionPill = true,
  showSourcedEmail = false,
}) => {
  // `at` exists purely to give each copy a fresh object identity. Keying the state on
  // the address alone means a second copy of the SAME address is `Object.is`-equal,
  // React bails out, the reset effect never re-runs, and the first timer keeps running
  // — so re-copying could clear the confirmation almost instantly.
  const [copied, setCopied] = React.useState<{ email: string; at: number }>()
  const { creator_data } = creator
  const country = creator_data?.location?.country
  const genderWord =
    creator_data?.gender === 'MALE' ? 'Male' : creator_data?.gender === 'FEMALE' ? 'Female' : null
  const isOnPlatform = creator.on_platform ?? false
  const platformGlyph = PROVIDER_GLYPH[creator.channel_type] ?? 'social-instagram-colour'
  const { isRealName, displayName = '' } = resolveCreatorName(creator)

  const genderAge = [genderWord, creator_data?.age_group]
    .filter((v) => v != null && v !== '')
    .join(', ')
  const metaLine = [country?.name, genderAge].filter(Boolean).join(' · ')

  // Only a real address earns the row. `_first_email` yields null whenever OnSocial
  // carries no email contact, and an empty row would leave a stray Copy button
  // attached to nothing (BRAND-9635). The address the creator gave when responding
  // beats the OnSocial one (BRAND-9722) — `||` not `??`, so a blank response falls
  // through to OnSocial rather than hiding it.
  const sourcedEmail = showSourcedEmail
    ? creator.creator_intent?.response_email?.trim() || creator_data?.email?.trim() || undefined
    : undefined

  // Storing the address rather than a boolean makes the confirmation self-invalidating:
  // the drawer stays mounted while the CM steps through creators, and a different
  // address simply is not the one that was copied — so the next creator cannot open
  // already reading "Copied". Mirrors how hooks/useCopyToClipboard models the same
  // problem. Confirming in place rather than via Toaster (the house pattern for the
  // app's other copy affordances) is deliberate: a toast renders underneath this
  // drawer panel, where the CM would never see it.
  const handleCopyEmail = async (): Promise<void> => {
    if (!sourcedEmail) return
    try {
      await clipboard.copy(sourcedEmail)
      setCopied({ email: sourcedEmail, at: Date.now() })
    } catch (error) {
      // clipboard-copy rejects when writeText is denied (document not focused,
      // permission refused) and again if the execCommand fallback fails. Clearing
      // rather than swallowing matters here: on failure the clipboard still holds
      // whatever was copied last — possibly the PREVIOUS creator's address — so a
      // label that kept saying "Copied" would invite pasting the wrong person's
      // email into outreach. The address stays selectable for a manual copy.
      console.warn('Copy failed', error)
      setCopied(undefined)
    }
  }

  // Time-limit the confirmation: the clipboard can be overwritten from outside this
  // app while the drawer sits open, and a stale "Copied" would misrepresent what a
  // paste would actually produce.
  React.useEffect(() => {
    if (!copied) return undefined
    const timer = setTimeout(() => setCopied(undefined), COPIED_RESET_MS)
    return () => clearTimeout(timer)
  }, [copied])

  // Non-pending creators render the status pill even with no action links/handlers —
  // unless the owning view has opted out, in which case only real handlers keep the
  // stack alive (the shortlist button still belongs on List/Shortlisted).
  const showPill = creator.status !== 'pending' && showDecisionPill
  const hasActions = Boolean(
    onApprove || onReject || onUndo || onShortlist || onUnshortlist || showPill,
  )

  return (
    <HeaderRoot>
      <HeaderAvatarBlock>
        <HeaderAvatar src={creator_data?.picture ?? ''} alt={displayName} />
        <HeaderPlatformBadge aria-hidden="true">
          <Icon glyph={platformGlyph} size={1.25} />
        </HeaderPlatformBadge>
      </HeaderAvatarBlock>

      <HeaderInfo>
        <HeaderName style={isRealName ? { textTransform: 'capitalize' } : undefined}>
          {displayName}
        </HeaderName>
        {metaLine && <HeaderMeta>{metaLine}</HeaderMeta>}
        {sourcedEmail && (
          <HeaderEmailRow data-testid="drawer-sourced-email">
            {/* No field label — an address is self-evident, and the meta line above
                carries no label either (BRAND-9635). */}
            <HeaderEmailValue>{sourcedEmail}</HeaderEmailValue>
            <HeaderCopyButton
              type="button"
              onClick={handleCopyEmail}
              // The row carries no text label, so without this the control's
              // accessible name is a bare "Copy" with nothing tying it to the address.
              aria-label={`Copy ${sourcedEmail}`}
              data-testid="drawer-sourced-email-copy"
            >
              <FormattedMessage
                id={
                  copied?.email === sourcedEmail
                    ? 'creatorSearch.drawer.identity.emailCopied'
                    : 'creatorSearch.drawer.identity.emailCopy'
                }
              />
            </HeaderCopyButton>
          </HeaderEmailRow>
        )}
        <HeaderPillRow>
          <HeaderPlatformPill $onPlatform={isOnPlatform}>
            {isOnPlatform ? (
              <FormattedMessage id="creatorSearch.row.onPlatform" />
            ) : (
              <FormattedMessage id="creatorSearch.row.offPlatform" />
            )}
          </HeaderPlatformPill>
          {creator.metadata?.yt_roster && (
            <HeaderRosterPill data-testid="drawer-header-roster-pill">
              <FormattedMessage id="creatorSearch.vibeCheck.row.source.yt_roster" />
            </HeaderRosterPill>
          )}
        </HeaderPillRow>
      </HeaderInfo>

      {hasActions && (
        <ActionStack>
          {showPill ? (
            <VibeCheckStatusPill status={creator.status} onUndo={onUndo} />
          ) : creator.status === 'pending' ? (
            <>
              {onApprove && (
                <HeaderActionButton
                  variant="approve"
                  onClick={onApprove}
                  disabled={isActionLoading}
                  data-testid="drawer-approve"
                >
                  <Icon glyph="tick-heavy" size={0.875} color="#01D36D" />
                  <FormattedMessage id="creatorSearch.vibeCheck.row.action.approve" />
                </HeaderActionButton>
              )}
              {onReject && (
                <HeaderActionButton
                  variant="reject"
                  onClick={onReject}
                  disabled={isActionLoading}
                  data-testid="drawer-reject"
                >
                  <Icon glyph="close-x" size={0.875} color="#FC5151" />
                  <FormattedMessage id="creatorSearch.vibeCheck.row.action.reject" />
                </HeaderActionButton>
              )}
            </>
          ) : null}
          {onShortlist && (
            <HeaderShortlistButton
              onClick={onShortlist}
              disabled={isActionLoading}
              data-testid="drawer-shortlist"
            >
              <Icon glyph="heart" size={1} color="#FC5151" />
              <FormattedMessage id="creatorSearch.row.action.shortlist" />
            </HeaderShortlistButton>
          )}
          {onUnshortlist && (
            <HeaderShortlistButton
              onClick={onUnshortlist}
              disabled={isActionLoading}
              data-testid="drawer-unshortlist"
            >
              <Icon glyph="heart-solid" size={1} color="#FC5151" />
              <FormattedMessage id="creatorSearch.row.action.unshortlist" />
            </HeaderShortlistButton>
          )}
        </ActionStack>
      )}
    </HeaderRoot>
  )
}

export { Header }
export default Header
